import {
	Badge as BadgeIcon,
	Business as BusinessIcon,
	Email as EmailIcon,
	Person as PersonIcon,
	Phone as PhoneIcon,
	Work as WorkIcon
} from "@mui/icons-material";
import { Avatar, Box, Grid2, Typography } from "@mui/material";
import React, { FC, useMemo } from "react";

import { useAppSelector } from "../../redux";
import { useGetUserSettingsQuery } from "../../redux/reducers/settings.reducer";
import { AGDialog } from "../AGDialog";
import "./index.css";

interface IProfileDialogProps {
	open: boolean;
	onClose: () => void;
}

interface IProfileInfoItemProps {
	icon: React.ReactNode;
	label: string;
	value: string;
}

const ProfileInfoItem: FC<IProfileInfoItemProps> = ({ icon, label, value }) => (
	<Box className="profile-info-item">
		<Box className="profile-info-item-icon">{icon}</Box>

		<Box className="profile-info-item-details">
			<Typography variant="body2" classes={{ root: "label" }}>
				{label}
			</Typography>

			<Typography variant="body1" classes={{ root: "value" }}>
				{value || "Not Available"}
			</Typography>
		</Box>
	</Box>
);

const ProfileDialog: FC<IProfileDialogProps> = (props) => {
	const { open, onClose } = props;

	const authState = useAppSelector((state) => state.auth);

	// APIS
	// GET USER SETTINGS
	const { data: profileDetails } = useGetUserSettingsQuery();

	const fullName = useMemo<string>(
		() => (profileDetails ? `${profileDetails.first_name} ${profileDetails.last_name}` : authState.user.full_name),
		[authState.user.full_name, profileDetails]
	);

	return (
		<AGDialog open={open} onClose={onClose} title="Profile Information" color="primary" maxWidth="sm">
			<Box>
				<Grid2 container spacing={2}>
					{/* Profile Header */}
					<Grid2 size={12}>
						<Box className="profile-header-wrapper">
							<Avatar classes={{ root: "profile-header-avatar" }}>
								{fullName
									.split(" ")
									.map((item) => item.slice(0, 1))
									.join("")}
							</Avatar>

							<Box sx={{ textAlign: "center" }}>
								<Typography variant="h5" fontWeight={600}>
									{fullName}
								</Typography>

								<Typography variant="body2" color="textSecondary">
									{profileDetails?.email || authState.user.email}
								</Typography>
							</Box>
						</Box>
					</Grid2>

					{/* Profile Details */}
					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem
							icon={<BadgeIcon sx={{ fontSize: "20px" }} />}
							label="Username"
							value={profileDetails?.username || authState.user.username}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem icon={<PersonIcon sx={{ fontSize: "20px" }} />} label="Name" value={fullName} />
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem
							icon={<EmailIcon sx={{ fontSize: "20px" }} />}
							label="Email Address"
							value={profileDetails?.email || authState.user.email}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem
							icon={<PhoneIcon sx={{ fontSize: "20px" }} />}
							label="Mobile Number"
							value={profileDetails?.phone_number || "Not Available"}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem
							icon={<BusinessIcon sx={{ fontSize: "20px" }} />}
							label="Role"
							value={authState.user.group_name || "--"}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12, md: 6 }}>
						<ProfileInfoItem
							icon={<WorkIcon sx={{ fontSize: "20px" }} />}
							label="Designation"
							value={profileDetails?.designation || "--"}
						/>
					</Grid2>
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default ProfileDialog;
